import { lotsServices } from "../../../../../api/lots";
import type { ApiStockLot } from "../../../../../api/lots";
import type { StockPlacementContent } from "../../../../../api/storePlaces";

/**
 * Тип складского документа «Приходный ордер» для `DocTypes` в `stock/lots.loadByFilter`
 * при скане партии в размещении ТМЦ.
 */
const STOCK_ORDER_DOC_TYPE = 1024;

/** Результат поиска строки состава размещения по скану партии. */
export type StockPlacementContentScanResult =
    | { type: "notFound" }
    | { type: "single"; contentCode: number }
    | { type: "ambiguous"; contentCodes: number[] };

/**
 * Ищет строку состава приходного ордера по скану номера партии. \
 * `lots.loadByFilter` `{ SoNum, DocTypes: [1024] }` → матч строки по `BaseCode`, \
 * сверка `DocCode === orderCode`. Отдельная сверка ТМЦ / kanban-контейнер не нужны.
 * @param barcode отсканированный номер партии (`SoNum`)
 * @param contents текущий состав приходного ордера (уже загружен)
 * @param orderCode код текущего приходного ордера
 * @returns результат разрешения скана в строку состава
 */
export async function resolveStockPlacementContentScan(
    barcode: string,
    contents: StockPlacementContent[],
    orderCode: number,
): Promise<StockPlacementContentScanResult> {
    const lots = uniqueLots(
        await lotsServices.load({
            flt: {
                SoNum: barcode,
                DocTypes: [STOCK_ORDER_DOC_TYPE],
            },
        }),
    );

    const matchedContents = matchContentsByLots(lots, contents, orderCode);

    if (matchedContents.length === 0) {
        return { type: "notFound" };
    }

    if (matchedContents.length === 1) {
        return { type: "single", contentCode: matchedContents[0].Code };
    }

    return { type: "ambiguous", contentCodes: matchedContents.map((content) => content.Code) };
}

/**
 * Сопоставляет партии со строками ПО: `lot.DocCode === orderCode` и `content.Code === lot.BaseCode`. \
 * Сверка по `LotCode` возможна, но `BaseCode` надёжнее.
 * @param lots партии из `lots.loadByFilter`
 * @param contents строки состава текущего ПО
 * @param orderCode код текущего ПО
 */
function matchContentsByLots(
    lots: ApiStockLot[],
    contents: StockPlacementContent[],
    orderCode: number,
): StockPlacementContent[] {
    const matched = new Map<number, StockPlacementContent>();

    lots.forEach((lot) => {
        if (!isValidCode(lot.BaseCode) || lot.DocCode !== orderCode) {
            return;
        }

        contents.forEach((content) => {
            if (content.Code === lot.BaseCode) {
                matched.set(content.Code, content);
            }
        });
    });

    return [...matched.values()];
}

function uniqueLots(lots: ApiStockLot[]): ApiStockLot[] {
    const result: ApiStockLot[] = [];
    const seen = new Set<number>();

    lots.forEach((lot) => {
        if (!seen.has(lot.LotCode)) {
            seen.add(lot.LotCode);
            result.push(lot);
        }
    });

    return result;
}

function isValidCode(code: number): boolean {
    return code !== 0;
}
